import type { INutrientItem } from "@/types/Nutrition.types";

export type NutrientRowValidationOptions = {
  isDualColumn?: boolean;
  isPetFood?: boolean;
};

const isBlank = (value: unknown): boolean =>
  value == null || String(value).trim() === "";

/** Strip display asterisk and normalize for matching. */
export const normalizeNutrientName = (name?: string | null): string =>
  String(name ?? "")
    .replace(" *", "")
    .trim()
    .toLowerCase();

/**
 * Whether a nutrient row is incomplete for section/table validation.
 * Mirrors rules in useNutritionValidation (non–pet food + pet food).
 */
export const isNutrientRowIncomplete = (
  row: INutrientItem | null | undefined,
  options: NutrientRowValidationOptions = {}
): boolean => {
  if (!row) return true;

  if (options.isPetFood) {
    return isBlank((row as any)?.valueToAppearOnLabel);
  }

  if (isBlank(row.nutrient)) return true;
  if (isBlank(row.nutritionUnitOfMeasure)) return true;
  if (isBlank(row.unroundedPer100gAsSoldColumn1)) return true;
  if (isBlank(row.unroundedPerServingAsSoldColumn1)) return true;

  if (options.isDualColumn) {
    if (isBlank(row.unroundedPer100gAsSoldColumn2)) return true;
    if (isBlank(row.unroundedPerServingAsSoldColumn2)) return true;
  }

  return false;
};

/**
 * Normalized names of incomplete nutrient rows (for table highlighting).
 * Rows with empty nutrient name are skipped as keys (cannot match in the grid).
 */
export const getInvalidNutrientNames = (
  nutrients: INutrientItem[] | null | undefined,
  options: NutrientRowValidationOptions = {}
): string[] => {
  const names: string[] = [];
  for (const row of nutrients || []) {
    if (!isNutrientRowIncomplete(row, options)) continue;
    const key = normalizeNutrientName(row?.nutrient);
    if (key) names.push(key);
  }
  return names;
};
